import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, fetchMe, logout } from "@/lib/auth";
import { AdminHeader, AdminSidebar } from "@/components/admin/AdminChrome";
import ContractsPanel from "@/components/admin/ContractsPanel";
import VendorStatsPanel from "@/components/admin/VendorStatsPanel";
import ListingEditsPanel from "@/components/admin/ListingEditsPanel";
import TasksPanel from "@/components/admin/TasksPanel";
import EnquiryPanel, {
  EnquiryDetailDrawer,
} from "@/components/admin/EnquiryPanel";
import DocumentsPanel from "@/components/admin/DocumentsPanel";
import VendorDetailDrawer from "@/components/admin/VendorDetailDrawer";
import CoupleDetailDrawer from "@/components/admin/CoupleDetailDrawer";
import DashboardPanel from "@/components/admin/DashboardPanel";
import VendorFormDataPanel from "@/components/admin/VendorFormDataPanel";
import CouplesFormDataPanel from "@/components/admin/CouplesFormDataPanel";
import CalendarPanel from "@/components/admin/CalendarPanel";
import ConsultationPanel from "@/components/admin/ConsultationPanel";
import VendorListPanel from "@/components/admin/VendorListPanel";
import CoupleListPanel from "@/components/admin/CoupleListPanel";
import SigninActivityPanel from "@/components/admin/SigninActivityPanel";
import { devLog } from "@/lib/log";
import BackupPanel from "@/components/admin/BackupPanel";
import { computeReviewState } from "@/components/admin/shared";

const Admin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [authChecked, setAuthChecked] = useState(false);
  const [section, setSection] = useState("dashboard");

  // Gate the entire dashboard behind admin auth. Three cases:
  //  - Not signed in → bounce to /signin?next=/admin
  //  - Signed in as couple/vendor → bounce them to their own home with a toast
  //    (no role can view the admin dashboard except admin)
  //  - Signed in as admin → allow
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const me = await fetchMe();
      if (cancelled) return;
      if (!me) {
        navigate("/signin?next=/admin", { replace: true });
        return;
      }
      if (me.role !== "admin") {
        toast("The admin dashboard is restricted.", { duration: 2600 });
        if (me.role === "couple") navigate("/my-favourites", { replace: true });
        else if (me.role === "vendor") navigate("/", { replace: true });
        else navigate("/", { replace: true });
        return;
      }
      setUser(me);
      setAuthChecked(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const [vendorSubTab, setVendorSubTab] = useState("pending");
  const [coupleSubTab, setCoupleSubTab] = useState("pending");

  const [vendors, setVendors] = useState([]);
  const [couples, setCouples] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [loadingV, setLoadingV] = useState(true);
  const [loadingC, setLoadingC] = useState(true);
  const [loadingE, setLoadingE] = useState(true);
  const [loadingT, setLoadingT] = useState(true);
  const [loadingS, setLoadingS] = useState(true);
  const [activeVendor, setActiveVendor] = useState(null);
  const [activeCouple, setActiveCouple] = useState(null);
  const [activeEnquiry, setActiveEnquiry] = useState(null);

  // Server-side pagination for admin lists. Page size matches the largest
  // batch the table can realistically render without lag (50 rows). The
  // back-end already caps `limit` at 1000 and returns `X-Total-Count`.
  // The vendor / couple form-data panels carry their own sub-tab status,
  // which we pass through so each tab paginates independently.
  const PAGE_SIZE = 50;
  const [vendorPage, setVendorPage] = useState(0);
  const [vendorTotal, setVendorTotal] = useState(0);
  const [vendorListStatus, setVendorListStatus] = useState("approved");
  const [vendorQuery, setVendorQuery] = useState("");
  const [couplePage, setCouplePage] = useState(0);
  const [coupleTotal, setCoupleTotal] = useState(0);
  const [coupleListStatus, setCoupleListStatus] = useState("approved");
  const [coupleQuery, setCoupleQuery] = useState("");
  const [listCounts, setListCounts] = useState({
    vendor: { pending: 0, approved: 0, rejected: 0, total: 0 },
    couple: { pending: 0, approved: 0, rejected: 0, total: 0 },
  });

  const reloadListCounts = async () => {
    try {
      const r = await api.get("/admin/list-counts");
      setListCounts(r.data || listCounts);
    } catch (e) {
      devLog("ld_admin list-counts fetch failed:", e?.message);
    }
  };

  const reloadVendors = async (
    page = vendorPage,
    status = vendorListStatus,
    q = vendorQuery,
  ) => {
    setLoadingV(true);
    try {
      const qs = new URLSearchParams({
        limit: String(PAGE_SIZE),
        skip: String(page * PAGE_SIZE),
      });
      if (status) qs.set("status", status);
      if (q) qs.set("q", q);
      const r = await api.get(`/admin/vendor-applications?${qs.toString()}`);
      setVendors(r.data || []);
      const total = parseInt(r.headers?.["x-total-count"] || "0", 10);
      if (!Number.isNaN(total)) setVendorTotal(total);
      setVendorPage(page);
      setVendorListStatus(status);
      setVendorQuery(q);
    } catch (e) {
      toast("Couldn't load vendor applications", { duration: 2800 });
    } finally {
      setLoadingV(false);
    }
  };

  const reloadCouples = async (
    page = couplePage,
    status = coupleListStatus,
    q = coupleQuery,
  ) => {
    setLoadingC(true);
    try {
      const qs = new URLSearchParams({
        limit: String(PAGE_SIZE),
        skip: String(page * PAGE_SIZE),
      });
      if (status) qs.set("status", status);
      if (q) qs.set("q", q);
      const r = await api.get(`/admin/couple-signups?${qs.toString()}`);
      setCouples(r.data || []);
      const total = parseInt(r.headers?.["x-total-count"] || "0", 10);
      if (!Number.isNaN(total)) setCoupleTotal(total);
      setCouplePage(page);
      setCoupleListStatus(status);
      setCoupleQuery(q);
    } catch (e) {
      toast("Couldn't load couple signups", { duration: 2800 });
    } finally {
      setLoadingC(false);
    }
  };

  const reloadCalendarEvents = async () => {
    try {
      const r = await api.get("/admin/calendar/events");
      setCalendarEvents(r.data || []);
    } catch (e) {
      devLog("ld_admin calendar events fetch failed:", e?.message);
    }
  };

  const reloadEnquiries = async () => {
    setLoadingE(true);
    try {
      const r = await api.get("/admin/enquiries");
      setEnquiries(r.data || []);
    } catch (e) {
      toast("Couldn't load enquiries", { duration: 2800 });
    } finally {
      setLoadingE(false);
    }
  };

  const updateEnquiryStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}/status`, { status });
      setEnquiries((rows) =>
        rows.map((r) => (r.id === id ? { ...r, status } : r))
      );
    } catch (e) {
      toast("Could not update enquiry", { duration: 2400 });
    }
  };

  const deleteEnquiry = async (id) => {
    try {
      await api.delete(`/admin/enquiries/${id}`);
      setEnquiries((rows) => rows.filter((r) => r.id !== id));
      setActiveEnquiry(null);
    } catch (e) {
      toast("Could not delete enquiry", { duration: 2400 });
    }
  };

  const reloadTasks = async () => {
    setLoadingT(true);
    try {
      const r = await api.get("/admin/tasks");
      setTasks(r.data || []);
    } catch (e) {
      toast("Couldn't load tasks", { duration: 2800 });
    } finally {
      setLoadingT(false);
    }
  };

  const createTask = async (payload) => {
    try {
      const r = await api.post("/admin/tasks", payload);
      setTasks((rows) => [r.data, ...rows]);
      toast("Task added", { duration: 1800 });
    } catch (e) {
      toast(e?.response?.data?.detail || "Could not add task", { duration: 2400 });
    }
  };

  const updateTask = async (id, patch) => {
    try {
      const r = await api.patch(`/admin/tasks/${id}`, patch);
      setTasks((rows) => rows.map((t) => (t.id === id ? r.data : t)));
    } catch (e) {
      toast(e?.response?.data?.detail || "Could not update task", { duration: 2400 });
    }
  };

  const deleteTask = async (id) => {
    try {
      await api.delete(`/admin/tasks/${id}`);
      setTasks((rows) => rows.filter((t) => t.id !== id));
    } catch (e) {
      toast("Could not delete task", { duration: 2400 });
    }
  };

  const reloadConsultations = async () => {
    setLoadingS(true);
    try {
      const r = await api.get("/admin/consultations");
      setConsultations(r.data || []);
    } catch (e) {
      toast("Couldn't load consultations", { duration: 2800 });
    } finally {
      setLoadingS(false);
    }
  };

  const toggleConsultation = async (row) => {
    try {
      const r = await api.patch(
        `/admin/consultations/${row.source}/${row.source_id}/complete`,
        { completed: !row.completed }
      );
      setConsultations((rows) =>
        rows.map((c) =>
          c.source === row.source && c.source_id === row.source_id
            ? r.data
            : c
        )
      );
      toast(
        !row.completed
          ? "Consultation marked complete"
          : "Consultation reopened",
        { duration: 1800 }
      );
    } catch (e) {
      toast(
        e?.response?.data?.detail || "Could not update consultation",
        { duration: 2400 }
      );
    }
  };

  const updateConsultationNotes = async (row, notes) => {
    try {
      const r = await api.patch(
        `/admin/consultations/${row.source}/${row.source_id}/notes`,
        { notes }
      );
      setConsultations((rows) =>
        rows.map((c) =>
          c.source === row.source && c.source_id === row.source_id
            ? r.data
            : c
        )
      );
    } catch (e) {
      toast(
        e?.response?.data?.detail || "Could not save notes",
        { duration: 2400 }
      );
    }
  };

  useEffect(() => {
    if (!authChecked) return;
    reloadVendors();
    reloadCouples();
    reloadCalendarEvents();
    reloadEnquiries();
    reloadTasks();
    reloadConsultations();
    reloadListCounts();
    // Initial data fan-out once auth is confirmed; reload fns are stable per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChecked]);

  // Refetch the vendor list when the current admin section / sub-tab
  // changes so the server-side status filter matches what the user is
  // looking at. Resets to page 0 on every status change.
  useEffect(() => {
    if (!authChecked) return;
    if (section === "vendor-list") {
      // Roster view is approved-only.
      if (vendorListStatus !== "approved" || vendorPage !== 0) {
        reloadVendors(0, "approved");
      }
    } else if (section === "vendor-form-data") {
      if (vendorListStatus !== vendorSubTab || vendorPage !== 0) {
        reloadVendors(0, vendorSubTab);
      }
    }
    if (section === "couple-list") {
      if (coupleListStatus !== "approved" || couplePage !== 0) {
        reloadCouples(0, "approved");
      }
    } else if (section === "couples-form-data") {
      if (coupleListStatus !== coupleSubTab || couplePage !== 0) {
        reloadCouples(0, coupleSubTab);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, vendorSubTab, coupleSubTab, authChecked]);

  const updateVendorStatus = async (app, status) => {
    try {
      await api.patch(`/admin/vendor-applications/${app.id}/status`, {
        status,
      });
      // The row's bucket changes — remove from current page and refresh
      // the counts so the sub-tab badges stay accurate.
      setVendors((list) => list.filter((v) => v.id !== app.id));
      reloadListCounts();
      toast(`Vendor ${status}`, { duration: 2200 });
    } catch (e) {
      toast("Update failed", { duration: 2400 });
    }
  };

  const deleteVendorApplication = async (app) => {
    if (
      !window.confirm(
        `Permanently delete ${app.company_name}? This cannot be undone.`
      )
    ) {
      return;
    }
    try {
      await api.delete(`/admin/vendor-applications/${app.id}`);
      setVendors((rows) => rows.filter((v) => v.id !== app.id));
      if (activeVendor?.id === app.id) setActiveVendor(null);
      reloadListCounts();
      toast("Application deleted", { duration: 2000 });
    } catch (e) {
      toast(e?.response?.data?.detail || "Could not delete", {
        duration: 2400,
      });
    }
  };

  const deleteCoupleSignup = async (row) => {
    const label = row.couple_full_name || "this sign-up";
    if (
      !window.confirm(`Permanently delete ${label}? This cannot be undone.`)
    ) {
      return;
    }
    try {
      await api.delete(`/admin/couple-signups/${row.id}`);
      setCouples((rows) => rows.filter((c) => c.id !== row.id));
      if (activeCouple?.id === row.id) setActiveCouple(null);
      reloadConsultations();
      reloadCalendarEvents();
      reloadListCounts();
      toast("Sign-up deleted", { duration: 2000 });
    } catch (e) {
      toast(e?.response?.data?.detail || "Could not delete", {
        duration: 2400,
      });
    }
  };

  const deleteConsultation = async (row) => {
    const label = row.name || row.company || "this consultation";
    if (
      !window.confirm(
        `Delete the consultation for ${label}? This cannot be undone.`
      )
    ) {
      return;
    }
    try {
      await api.delete(
        `/admin/consultations/${row.source}/${row.source_id}`
      );
      setConsultations((rows) =>
        rows.filter(
          (c) =>
            !(c.source === row.source && c.source_id === row.source_id)
        )
      );
      // Slot deletes free a calendar event; signup deletes clear consultation fields
      reloadCalendarEvents();
      if (row.source === "signup") reloadCouples();
      toast("Consultation deleted", { duration: 2000 });
    } catch (e) {
      toast(e?.response?.data?.detail || "Could not delete", {
        duration: 2400,
      });
    }
  };

  // Review cycle: green -> red -> auto (cleared) -> green ...
  const cycleVendorReview = async (app) => {
    const current =
      app.review_override === "green" || app.review_override === "red"
        ? app.review_override
        : null;
    let next;
    if (current === null) next = "green";
    else if (current === "green") next = "red";
    else next = null;
    try {
      await api.patch(`/admin/vendor-applications/${app.id}/review`, {
        review_override: next,
      });
      setVendors((list) =>
        list.map((v) =>
          v.id === app.id ? { ...v, review_override: next } : v
        )
      );
    } catch (e) {
      toast("Review update failed", { duration: 2400 });
    }
  };

  // Mark review complete: resets the 10-month timer + clears override.
  const markVendorReviewComplete = async (app) => {
    try {
      const { data } = await api.post(
        `/admin/vendor-applications/${app.id}/mark-review-complete`
      );
      setVendors((list) =>
        list.map((v) =>
          v.id === app.id
            ? {
                ...v,
                last_reviewed_at: data.last_reviewed_at,
                review_override: null,
              }
            : v
        )
      );
      toast("Review marked complete", { duration: 2200 });
    } catch (e) {
      toast("Could not mark review complete", { duration: 2400 });
    }
  };

  const updateCoupleStatus = async (row, status) => {
    try {
      await api.patch(`/admin/couple-signups/${row.id}/status`, { status });
      // Row moves out of the current sub-tab — drop it locally and
      // refresh the counts so the sub-tab badges stay accurate.
      setCouples((list) => list.filter((c) => c.id !== row.id));
      reloadListCounts();
      toast(`Marked ${status}`, { duration: 2200 });
    } catch (e) {
      toast("Update failed", { duration: 2400 });
    }
  };

  const updateCoupleConsultation = async (row, payload) => {
    try {
      const { data } = await api.patch(
        `/admin/couple-signups/${row.id}/consultation`,
        payload
      );
      setCouples((list) =>
        list.map((c) => (c.id === row.id ? { ...c, ...data } : c))
      );
      // Refresh calendar events whenever consultation changes
      reloadCalendarEvents();
      reloadConsultations();
      if (payload.consultation_offered === "yes" && payload.consultation_date) {
        toast(
          `Consultation scheduled for ${payload.consultation_date}`,
          { duration: 2400 }
        );
      }
    } catch (e) {
      toast("Could not update consultation", { duration: 2400 });
    }
  };

  const counts = useMemo(
    () => ({
      pending: listCounts.vendor.pending,
      approved: listCounts.vendor.approved,
      rejected: listCounts.vendor.rejected,
      vendorsTotal: listCounts.vendor.total,
      couplesPending: listCounts.couple.pending,
      couplesApproved: listCounts.couple.approved,
      couplesRejected: listCounts.couple.rejected,
      couplesTotal: listCounts.couple.total,
    }),
    [listCounts]
  );

  // With server-side status filtering the `vendors` / `couples` arrays
  // already contain exactly the rows for the active sub-tab. We expose
  // them under the legacy names to keep the panel components untouched.
  const vendorsForTab = vendors;
  const couplesForTab = couples;
  // The dashboard's "vendors needing review" highlight previously
  // scanned the full active set — we now scan the currently-loaded page.
  // Good enough at any practical scale: only red flags on the visible
  // batch surface in the dashboard, and the operator clicks through to
  // the review tab where pagination shows the rest.
  const vendorsNeedingReview = useMemo(
    () => vendors.filter((v) => computeReviewState(v) === "red"),
    [vendors]
  );

  /* Memoized pagination props so child panels don't re-render on
     unrelated state changes. */
  const vendorTabPagination = useMemo(
    () => ({
      page: vendorPage,
      pageSize: PAGE_SIZE,
      total: vendorTotal,
      onPageChange: (p) => reloadVendors(p, vendorSubTab),
      loading: loadingV,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vendorPage, vendorTotal, loadingV, vendorSubTab]
  );
  const coupleTabPagination = useMemo(
    () => ({
      page: couplePage,
      pageSize: PAGE_SIZE,
      total: coupleTotal,
      onPageChange: (p) => reloadCouples(p, coupleSubTab),
      loading: loadingC,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [couplePage, coupleTotal, loadingC, coupleSubTab]
  );
  const vendorListPagination = useMemo(
    () => ({
      page: vendorPage,
      pageSize: PAGE_SIZE,
      total: vendorTotal,
      onPageChange: (p) => reloadVendors(p, "approved"),
      loading: loadingV,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vendorPage, vendorTotal, loadingV]
  );
  const coupleListPagination = useMemo(
    () => ({
      page: couplePage,
      pageSize: PAGE_SIZE,
      total: coupleTotal,
      onPageChange: (p) => reloadCouples(p, "approved"),
      loading: loadingC,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [couplePage, coupleTotal, loadingC]
  );

  const handleLogout = async () => {
    await logout();
    toast("Signed out", { duration: 1800 });
    navigate("/", { replace: true });
  };

  if (!authChecked) {
    return (
      <main
        data-testid="admin-auth-loading"
        className="min-h-[100dvh] flex items-center justify-center"
        style={{ backgroundColor: "var(--ld-ivory)" }}
      >
        <span className="font-accent text-[11px] tracking-[0.25em] uppercase text-[var(--ld-ink-muted)]">
          Signing you in…
        </span>
      </main>
    );
  }

  return (
    <main
      data-testid="admin-page"
      className="relative min-h-[100dvh] w-full overflow-x-hidden flex flex-col"
      style={{ backgroundColor: "var(--ld-ivory)" }}
    >
      {/* Top bar */}
      <AdminHeader user={user} onLogout={handleLogout} />

      <div className="flex-1 flex flex-col md:flex-row gap-0 md:gap-10 px-6 md:px-12 py-10 md:py-14">
        <AdminSidebar section={section} onSetSection={setSection} />

        {/* Content */}
        <section className="flex-1 min-w-0 mt-10 md:mt-0">
          <div className="max-w-7xl">
            {section === "dashboard" && (
              <DashboardPanel
                user={user}
                counts={counts}
                vendorsNeedingReview={vendorsNeedingReview}
                onSetSection={setSection}
                onSetVendorSubTab={setVendorSubTab}
                onMarkReviewComplete={markVendorReviewComplete}
                reloadVendors={reloadVendors}
                reloadCouples={reloadCouples}
              />
            )}

            {section === "vendor-form-data" && (
              <VendorFormDataPanel
                vendorSubTab={vendorSubTab}
                setVendorSubTab={setVendorSubTab}
                counts={listCounts.vendor}
                vendorsForTab={vendorsForTab}
                loadingV={loadingV}
                reloadVendors={reloadVendors}
                onStatus={updateVendorStatus}
                onView={setActiveVendor}
                onCycleReview={cycleVendorReview}
                onMarkReviewComplete={markVendorReviewComplete}
                onDelete={deleteVendorApplication}
                query={vendorQuery}
                onSearch={(q) => reloadVendors(0, vendorSubTab, q)}
                pagination={vendorTabPagination}
              />
            )}

            {section === "couples-form-data" && (
              <CouplesFormDataPanel
                coupleSubTab={coupleSubTab}
                setCoupleSubTab={setCoupleSubTab}
                counts={listCounts.couple}
                couplesForTab={couplesForTab}
                loadingC={loadingC}
                reloadCouples={reloadCouples}
                onStatus={updateCoupleStatus}
                onView={setActiveCouple}
                onConsultation={updateCoupleConsultation}
                onDelete={deleteCoupleSignup}
                query={coupleQuery}
                onSearch={(q) => reloadCouples(0, coupleSubTab, q)}
                pagination={coupleTabPagination}
              />
            )}

            {section === "calendar" && (
              <CalendarPanel
                events={calendarEvents}
                onRefresh={reloadCalendarEvents}
              />
            )}

            {section === "enquiry" && (
              <EnquiryPanel
                enquiries={enquiries}
                loading={loadingE}
                onView={setActiveEnquiry}
                onStatus={updateEnquiryStatus}
                onDelete={deleteEnquiry}
                onRefresh={reloadEnquiries}
              />
            )}

            {section === "vendor-list" && (
              <VendorListPanel
                vendors={vendors}
                consultations={consultations}
                onView={setActiveVendor}
                pagination={vendorListPagination}
                onVendorUpdated={(updated, opts) => {
                  setVendors((rows) =>
                    rows.map((v) => {
                      if (v.id === updated.id) {
                        return { ...v, ...updated };
                      }
                      // Featured is exclusive — when one vendor flips to
                      // featured, clear the flag on every other vendor.
                      if (opts?.exclusiveFeatured && v.is_featured) {
                        return { ...v, is_featured: false };
                      }
                      return v;
                    })
                  );
                }}
              />
            )}

            {section === "couple-list" && (
              <CoupleListPanel
                couples={couples}
                onView={setActiveCouple}
                pagination={coupleListPagination}
              />
            )}

            {section === "tasks" && (
              <TasksPanel
                tasks={tasks}
                loading={loadingT}
                onCreate={createTask}
                onUpdate={updateTask}
                onDelete={deleteTask}
                onRefresh={reloadTasks}
              />
            )}

            {section === "consultation" && (
              <ConsultationPanel
                consultations={consultations}
                loading={loadingS}
                onToggle={toggleConsultation}
                onNotes={updateConsultationNotes}
                onDelete={deleteConsultation}
                onRefresh={reloadConsultations}
              />
            )}

            {section === "contracts" && <ContractsPanel />}
            {section === "vendor-stats" && <VendorStatsPanel />}
            {section === "listing-edits" && <ListingEditsPanel />}
            {section === "documents" && <DocumentsPanel />}
            {section === "signin-activity" && <SigninActivityPanel />}
            {section === "backup" && <BackupPanel />}
          </div>
        </section>
      </div>

      <VendorDetailDrawer
        app={activeVendor}
        onClose={() => setActiveVendor(null)}
        onUpdated={(updated) => {
          setVendors((rows) =>
            rows.map((v) => (v.id === updated.id ? { ...v, ...updated } : v))
          );
          setActiveVendor((prev) =>
            prev && prev.id === updated.id ? { ...prev, ...updated } : prev
          );
        }}
      />
      <CoupleDetailDrawer
        row={activeCouple}
        onClose={() => setActiveCouple(null)}
        onUpdated={(updated) => {
          setCouples((rows) =>
            rows.map((c) => (c.id === updated.id ? { ...c, ...updated } : c))
          );
          setActiveCouple(updated);
        }}
      />
      <EnquiryDetailDrawer
        row={activeEnquiry}
        onClose={() => setActiveEnquiry(null)}
        onStatus={updateEnquiryStatus}
        onDelete={deleteEnquiry}
      />

      <footer className="border-t border-[var(--ld-border)]/80 px-6 md:px-12 py-6 text-center text-xs tracking-[0.2em] uppercase text-[var(--ld-ink-muted)]">
        © {new Date().getFullYear()} · Luxé Directory · Admin
      </footer>
    </main>
  );
};

export default Admin;
